import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip
} from '@material-ui/core';
import {
  Trash2 as DeleteIcon,
  CheckCircle as ConfirmIcon,
  Unlock as ReleaseIcon,
  DollarSign as PayIcon,
  XCircle as CancelIcon
} from 'react-feather';
import {
  COMMISSION_STATUS,
  getStatusLabel,
  getStatusColor
} from '../../utils/commissionStatus';

const TERMINAL_STATUSES = [COMMISSION_STATUS.PAID, COMMISSION_STATUS.CANCELLED];

const SalesListResults = ({
  regs,
  page,
  setPage,
  perPage,
  setPerPage,
  total,
  onDelete,
  onConfirmPayment,
  onRelease,
  onPay,
  onCancel,
  isAdmin,
  ...rest
}) => {
  const handleLimitChange = (event) => {
    setPerPage(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);

  const renderCommissionActions = (sale) => {
    if (!isAdmin || !sale.commission || TERMINAL_STATUSES.includes(sale.commission.status)) {
      return null;
    }

    const commissionId = sale.commission.id;

    return (
      <>
        {sale.commission.status === COMMISSION_STATUS.PENDING_PAYMENT && (
          <Tooltip title="Confirmar Pagamento">
            <IconButton
              size="small"
              sx={{ color: 'info.main' }}
              onClick={() => onConfirmPayment(commissionId)}
            >
              <ConfirmIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
        {sale.commission.status === COMMISSION_STATUS.PENDING_RELEASE && (
          <Tooltip title="Liberar">
            <IconButton
              size="small"
              sx={{ color: 'primary.main' }}
              onClick={() => onRelease(commissionId)}
            >
              <ReleaseIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
        {sale.commission.status === COMMISSION_STATUS.RELEASED && (
          <Tooltip title="Marcar como Paga">
            <IconButton
              size="small"
              sx={{ color: 'success.main' }}
              onClick={() => onPay(commissionId)}
            >
              <PayIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Cancelar comissão">
          <IconButton
            size="small"
            sx={{ color: 'error.main' }}
            onClick={() => onCancel(commissionId)}
          >
            <CancelIcon size={18} />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  return (
    <Card {...rest}>
      <PerfectScrollbar>
        <Box sx={{ minWidth: 1050 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID Pedido</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>Afiliado</TableCell>
                {isAdmin && <TableCell>Valor</TableCell>}
                <TableCell>Data da Venda</TableCell>
                <TableCell>Comissão</TableCell>
                {isAdmin && <TableCell align="center">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {regs.map((sale) => (
                <TableRow hover key={sale.id}>
                  <TableCell>
                    <Typography color="textPrimary" variant="body1" sx={{ fontWeight: 'bold' }}>
                      {sale.external_order_id}
                    </Typography>
                    <Typography color="textSecondary" variant="body2" fontSize={12}>
                      ID: {sale.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="textPrimary" variant="body2">
                      {sale.affiliateLink?.channel?.name || 'N/A'}
                    </Typography>
                    <Typography color="textSecondary" variant="body2" fontSize={12}>
                      {sale.affiliateLink?.channel?.platform}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="textPrimary" variant="body2" sx={{ fontWeight: 'bold' }}>
                      {sale.affiliateLink?.code || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="textPrimary" variant="body2">
                      {sale.affiliateLink?.channel?.user?.name || 'N/A'}
                    </Typography>
                    <Typography color="textSecondary" variant="body2" fontSize={12}>
                      {sale.affiliateLink?.channel?.user?.email}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Typography color="textPrimary" variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(sale.amount)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography color="textPrimary" variant="body2">
                      {moment(sale.sold_at).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {sale.commission ? (
                      <Box>
                        <Typography
                          color="textPrimary"
                          variant="body2"
                          sx={{ fontWeight: 'bold', color: 'success.main' }}
                        >
                          {formatCurrency(sale.commission.amount)}
                        </Typography>
                        <Chip
                          label={getStatusLabel(sale.commission.status)}
                          color={getStatusColor(sale.commission.status)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography color="textSecondary" variant="body2">
                        Sem comissão
                      </Typography>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {renderCommissionActions(sale)}
                        <Tooltip title="Deletar venda">
                          <IconButton
                            size="small"
                            sx={{ color: 'error.dark' }}
                            onClick={() => onDelete(sale.id)}
                          >
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Registros por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
      />
    </Card>
  );
};

SalesListResults.propTypes = {
  regs: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  setPerPage: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
  onConfirmPayment: PropTypes.func,
  onRelease: PropTypes.func,
  onPay: PropTypes.func,
  onCancel: PropTypes.func,
  isAdmin: PropTypes.bool
};

export default SalesListResults;
